import { memo } from 'react';
import type { ReactNode } from 'react';
import {
  CardsGrid,
  CardContainer,
  CardHeader,
  CardChips,
  StatusChip,
  InfoRow,
  InfoLabel,
  InfoValue,
  InfoFieldsStack,
} from './styles';

//  Public types
export interface CardChipConfig {
  label: string;
  icon?: ReactNode;
  bgColor?: string;
  textColor?: string;
  size?: 'small' | 'medium';
}

export interface CardInfoField {
  key: string;
  label: string;
  value: ReactNode;
  icon?: ReactNode;
}

//  CardsGrid (wrapper)
export interface GenericCardsGridProps {
  gap?: string;
  /** Columns currently fitting a row; when set, every card (full or trailing row) is pinned to that exact width */
  columns?: number;
  children: ReactNode;
}

export function GenericCardsGrid({
  gap,
  columns,
  children,
}: GenericCardsGridProps) {
  return (
    <CardsGrid $gap={gap} $columns={columns}>
      {children}
    </CardsGrid>
  );
}

//  GenericDeviceCard
export interface GenericDeviceCardProps {
  chips?: CardChipConfig[];
  fields: CardInfoField[];
  onClick?: () => void;
  width?: string;
  headerExtra?: ReactNode;
}

/** Card for grid mode to display list of equipment, devices and others */
function GenericDeviceCard({
  chips = [],
  fields,
  onClick,
  width,
  headerExtra,
}: GenericDeviceCardProps) {
  return (
    <CardContainer $width={width} $clickable={!!onClick} onClick={onClick}>
      {/*  Chips header  */}
      {(chips.length > 0 || headerExtra) && (
        <CardHeader>
          <CardChips>
            {chips.map((chip) => (
              <StatusChip
                key={chip.label}
                label={chip.label}
                size={chip.size || 'small'}
                icon={chip.icon}
                $bgColor={chip.bgColor}
                $textColor={chip.textColor}
              />
            ))}
          </CardChips>
          {headerExtra}
        </CardHeader>
      )}

      {/*  Info fields  */}
      <InfoFieldsStack>
        {fields.map(
          (field) =>
            field.value !== null &&
            field.value !== undefined && (
              <InfoRow key={field.key}>
                {field.icon && <span className="info-icon">{field.icon}</span>}
                <div className="info-content">
                  <InfoValue>{field.value}</InfoValue>
                  <InfoLabel>{field.label}</InfoLabel>
                </div>
              </InfoRow>
            )
        )}
      </InfoFieldsStack>
    </CardContainer>
  );
}

export default memo(GenericDeviceCard);
